/**
 * OrderStatusCard Component
 * Displays an individual submitted order request and its latest processing status.
 * Automatically checks pending requests while retaining manual refresh.
 */

import {CheckCircle, Error, HourglassTop, Schedule, Refresh, Warning} from '@mui/icons-material'
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  useTheme
} from '@mui/material'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {formatQuantityDisplay} from '../../../shared/order-editor/utils/quantity.utils'

import {formattedDate} from '../../../../../Pages/Order/OrderDetails/components/utils'
import {useOrderStatusCheck, OrderStatusCheckResult} from '../../hooks/useOrderStatusCheck'
import {RmcOrder} from '../../types/order'
import {TransactionResultType} from '../../types/confirmationResult'

const AutomaticStatusCheckInterval = 5000
const MaximumAutomaticStatusChecks = 3

interface OrderStatusCardProps {
  order: RmcOrder
  requestId?: string
  initialStatus?: 'submitted' | 'failed'
  errorMessage?: string
  onAutomaticStatusCheckChange?: (isActive: boolean) => void
  onStatusChange?: (status: TransactionResultType | null) => void
}

export const OrderStatusCard: React.FC<OrderStatusCardProps> = ({
  order,
  requestId,
  initialStatus = 'submitted',
  errorMessage,
  onAutomaticStatusCheckChange,
  onStatusChange
}) => {
  const {t, i18n} = useTranslation()
  const theme = useTheme()
  const {checkOrderStatus, isLoading} = useOrderStatusCheck()
  const [statusResult, setStatusResult] = useState<OrderStatusCheckResult | null>(null)
  const [isAutomaticStatusCheckActive, setIsAutomaticStatusCheckActive] = useState(
    initialStatus === 'submitted' && Boolean(requestId)
  )
  const automaticStatusCheckCount = useRef(0)
  const statusColors = {
    blue: theme.palette.info.main,
    green: theme.palette.success.main,
    orange: theme.palette.warning.main,
    red: theme.palette.error.main
  }

  useEffect(() => {
    onAutomaticStatusCheckChange?.(isAutomaticStatusCheckActive)
  }, [isAutomaticStatusCheckActive, onAutomaticStatusCheckChange])

  useEffect(() => {
    onStatusChange?.(statusResult?.status ?? null)
  }, [onStatusChange, statusResult?.status])

  const handleCheckStatus = useCallback(async () => {
    if (!requestId) return undefined

    try {
      const result = await checkOrderStatus(requestId)
      setStatusResult(result)
      return result
    } catch (error) {
      console.error('Failed to check order status:', error)
      return undefined
    }
  }, [checkOrderStatus, requestId])

  const checkStatusAutomatically = useCallback(async () => {
    if (automaticStatusCheckCount.current >= MaximumAutomaticStatusChecks) return

    automaticStatusCheckCount.current += 1
    const result = await handleCheckStatus()
    const shouldRetry = result?.status === 'pending' || result?.status === 'pendingDispatch'

    if (!shouldRetry || automaticStatusCheckCount.current >= MaximumAutomaticStatusChecks) {
      setIsAutomaticStatusCheckActive(false)
    }
  }, [handleCheckStatus])

  useEffect(() => {
    if (initialStatus === 'submitted' && requestId && automaticStatusCheckCount.current === 0) {
      void checkStatusAutomatically()
    }
  }, [checkStatusAutomatically, initialStatus, requestId])

  useEffect(() => {
    const shouldRetry =
      statusResult?.status === 'pending' || statusResult?.status === 'pendingDispatch'

    if (!shouldRetry || automaticStatusCheckCount.current >= MaximumAutomaticStatusChecks) return

    const timeout = window.setTimeout(() => {
      void checkStatusAutomatically()
    }, AutomaticStatusCheckInterval)
    return () => window.clearTimeout(timeout)
  }, [checkStatusAutomatically, statusResult])

  const getCurrentStatus = () => {
    if (isAutomaticStatusCheckActive && statusResult) return 'checking'
    if (isLoading) return 'checking'
    if (statusResult?.error) return 'error'
    if (statusResult?.status) return statusResult.status
    if (initialStatus === 'failed') return 'failed'
    return 'submitted'
  }

  const getStatusHeaderText = () => {
    const status = getCurrentStatus()

    switch (status) {
      case 'success':
        return t('orderIntake.rmc.confirmation.statusHeaders.success')
      case 'pending':
        return t('orderIntake.rmc.confirmation.statusHeaders.pending')
      case 'pendingDispatch':
        return t('orderIntake.rmc.confirmation.statusHeaders.pendingDispatch')
      case 'customerActionRequired':
        return t('orderIntake.rmc.confirmation.statusHeaders.customerActionRequired')
      case 'failed':
      case 'failure':
        return t('orderIntake.rmc.confirmation.statusHeaders.failed')
      case 'checking':
        return t('orderIntake.rmc.confirmation.statusHeaders.checking')
      case 'error':
        return t('orderIntake.rmc.confirmation.statusHeaders.error')
      default:
        return t('orderIntake.rmc.confirmation.statusHeaders.submitted')
    }
  }

  const getStatusDescriptionText = () => {
    const status = getCurrentStatus()

    switch (status) {
      case 'success':
        return t('orderIntake.rmc.confirmation.statusDescriptions.success')
      case 'pending':
        return t('orderIntake.rmc.confirmation.statusDescriptions.pending')
      case 'pendingDispatch':
        return t('orderIntake.rmc.confirmation.statusDescriptions.pendingDispatch')
      case 'customerActionRequired':
        return t('orderIntake.rmc.confirmation.statusDescriptions.customerActionRequired')
      case 'failed':
      case 'failure':
        return errorMessage || t('orderIntake.rmc.confirmation.statusDescriptions.failed')
      case 'checking':
        return t('orderIntake.rmc.confirmation.statusDescriptions.checking')
      case 'error':
        return t('orderIntake.rmc.confirmation.statusDescriptions.error')
      default:
        return t('orderIntake.rmc.confirmation.statusDescriptions.submitted')
    }
  }

  const getStatusDisplay = () => {
    // Keep the card blue for the entire automatic polling lifecycle, including retry waits.
    if ((isAutomaticStatusCheckActive && statusResult) || isLoading) {
      return {
        icon: <HourglassTop sx={{color: statusColors.blue, fontSize: '20px'}} />,
        chip: (
          <Chip
            label={t('orderIntake.rmc.confirmation.chipLabels.checking')}
            color="info"
            size="small"
            icon={<CircularProgress data-test-id="automatic-status-check-spinner" size={16} />}
          />
        ),
        color: statusColors.blue
      }
    }

    // If we have a status check result, use that
    if (statusResult) {
      if (statusResult.error) {
        return {
          icon: <Error sx={{color: statusColors.red, fontSize: '20px'}} />,
          chip: (
            <Chip
              label={t('orderIntake.rmc.confirmation.chipLabels.checkFailed')}
              color="error"
              size="small"
            />
          ),
          color: statusColors.red
        }
      }

      switch (statusResult.status) {
        case 'success':
          return {
            icon: <CheckCircle sx={{color: statusColors.green, fontSize: '20px'}} />,
            chip: (
              <Chip
                label={t('orderIntake.rmc.confirmation.chipLabels.confirmed')}
                color="success"
                size="small"
              />
            ),
            color: statusColors.green
          }
        case 'pending':
          return {
            icon: <HourglassTop sx={{color: statusColors.orange, fontSize: '20px'}} />,
            chip: (
              <Chip
                label={t('orderIntake.rmc.confirmation.chipLabels.pending')}
                color="warning"
                size="small"
              />
            ),
            color: statusColors.orange
          }
        case 'pendingDispatch':
          return {
            icon: <Schedule sx={{color: statusColors.blue, fontSize: '20px'}} />,
            chip: (
              <Chip
                label={t('orderIntake.rmc.confirmation.chipLabels.pendingDispatch')}
                color="primary"
                size="small"
              />
            ),
            color: statusColors.blue
          }
        case 'customerActionRequired':
          return {
            icon: <Warning sx={{color: statusColors.orange, fontSize: '20px'}} />,
            chip: (
              <Chip
                label={t('orderIntake.rmc.confirmation.chipLabels.customerActionRequired')}
                color="warning"
                size="small"
              />
            ),
            color: statusColors.orange
          }
        case 'failure':
          return {
            icon: <Error sx={{color: statusColors.red, fontSize: '20px'}} />,
            chip: (
              <Chip
                label={t('orderIntake.rmc.confirmation.chipLabels.orderFailed')}
                color="error"
                size="small"
              />
            ),
            color: statusColors.red
          }
        default:
          return {
            icon: <HourglassTop sx={{color: statusColors.orange, fontSize: '20px'}} />,
            chip: (
              <Chip
                label={t('orderIntake.rmc.confirmation.chipLabels.unknownStatus')}
                color="warning"
                size="small"
              />
            ),
            color: statusColors.orange
          }
      }
    }

    // Default initial status
    if (initialStatus === 'failed') {
      return {
        icon: <Error sx={{color: statusColors.red, fontSize: '20px'}} />,
        chip: (
          <Chip
            label={t('orderIntake.rmc.confirmation.chipLabels.failedToPlace')}
            color="error"
            size="small"
          />
        ),
        color: statusColors.red
      }
    }

    // A successful placement response only confirms that the request was submitted.
    return {
      icon: <HourglassTop sx={{color: statusColors.orange, fontSize: '20px'}} />,
      chip: (
        <Chip
          label={t('orderIntake.rmc.confirmation.chipLabels.submitted')}
          color="warning"
          size="small"
          icon={
            isAutomaticStatusCheckActive ? (
              <CircularProgress data-test-id="automatic-status-check-spinner" size={16} />
            ) : undefined
          }
        />
      ),
      color: statusColors.orange
    }
  }

  const statusDisplay = getStatusDisplay()

  return (
    <Card
      sx={{
        marginBottom: '12px',
        border: `1px solid ${statusDisplay.color}`,
        '&:hover': {
          boxShadow: 2
        }
      }}
    >
      <CardContent sx={{paddingBottom: '16px !important'}}>
        {/* Order Details Row */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, marginBottom: '16px'}}>
          {statusDisplay.icon}

          <Box sx={{flex: 1}}>
            <Typography variant="body1" sx={{fontWeight: 500}}>
              {`${order.formData.material?.materialDescription} - ${formatQuantityDisplay(order.formData.quantity, t)}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {`${t('orderIntake.rmc.confirmation.deliveryLabel')} ${
                formattedDate(order.formData.dateTime[0], i18n.language)[0]
              } - ${formattedDate(order.formData.dateTime[0], i18n.language)[1] || ''}`}
            </Typography>
          </Box>
        </Box>

        {/* Order Status Section */}
        <Box sx={{backgroundColor: '#f5f5f5', borderRadius: '8px', padding: '16px'}}>
          {/* Status Header */}
          <Typography
            variant="h6"
            sx={{fontWeight: 600, marginBottom: '8px', color: statusDisplay.color}}
          >
            {getStatusHeaderText()}
          </Typography>

          {/* Status Description */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{marginBottom: '16px', lineHeight: 1.5}}
          >
            {getStatusDescriptionText()}
          </Typography>

          {/* Status Chip and Check Button Row */}
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>{statusDisplay.chip}</Box>

            {requestId && !isAutomaticStatusCheckActive && !isLoading && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={handleCheckStatus}
                sx={{minWidth: '120px'}}
              >
                {t('orderIntake.rmc.confirmation.checkStatusButton')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Error Messages */}
        {statusResult?.error && (
          <Alert severity="error" sx={{marginTop: '12px', padding: '8px 12px'}}>
            <Typography variant="body2">
              {t('orderIntake.rmc.confirmation.errorLabel')} {statusResult.error}
            </Typography>
          </Alert>
        )}

        {!requestId && initialStatus === 'submitted' && (
          <Alert severity="info" sx={{marginTop: '12px', padding: '8px 12px'}}>
            <Typography variant="body2">
              {t('orderIntake.rmc.confirmation.statusDescriptions.submitted')}
            </Typography>
          </Alert>
        )}

        {!requestId && initialStatus === 'failed' && errorMessage && (
          <Alert severity="error" sx={{marginTop: '12px', padding: '8px 12px'}}>
            <Typography variant="body2">
              {t('orderIntake.rmc.confirmation.errorLabel')} {errorMessage}
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}
